import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import FolderIcon from "../../../assets/images/folder.png";
import { Modal, Input } from 'antd';
import { baseUrl } from '../../../constants/api';
import { API_ENDPOINTS } from '../../../constants/endpoints';
import { useDispatch, useSelector } from 'react-redux';
import { setTopics, RootState } from '../../../store';
import BackBtn from '../../../assets/svg/backbtn.svg';
import showToastMessage from '../../../utils/showToastMessage';
import { FaPlus, FaTrash, FaEdit } from 'react-icons/fa';

interface Topic {
    name: string;
    subtopics: string[];
}

interface ItemToRename {
    name: string;
    isSubtopic: boolean;
}

interface DraggingItem {
    name: string;
    isSubtopic: boolean;
}

export default function QuestionFolders() {
    const navigate = useNavigate();
    const location = useLocation();
    const token = useSelector((state: RootState) => state.account.token);
    const [loading, setLoading] = useState(false);
    const [topics, setTopicsLocal] = useState<Topic[]>([]);
    const [currentFolder, setCurrentFolder] = useState<string | null>(null);
    const [subtopics, setSubtopics] = useState<string[]>([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [newItemName, setNewItemName] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [draggingItem, setDraggingItem] = useState<DraggingItem | null>(null);
    const [isRenameModalVisible, setIsRenameModalVisible] = useState(false);
    const [itemToRename, setItemToRename] = useState<ItemToRename | null>(null);
    const [newName, setNewName] = useState("");
    const dispatch = useDispatch();

    const fetchTopics = useCallback(async () => {
        const apiUrl = `${baseUrl}${API_ENDPOINTS.GET_ALL_TOPICS}`;
        setLoading(true);
        try {
            const response = await fetch(apiUrl, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const topicData = await response.json();
                const fetchedTopics: Topic[] = topicData.data.map((topic: any) => ({
                    name: topic.topic_name,
                    subtopics: topic.subtopics || [],
                }));
                setTopicsLocal(fetchedTopics);
                dispatch(setTopics(fetchedTopics));
            } else {
                const errorText = await response.text();
                showToastMessage(`Failed to fetch topics: ${errorText}`, "error");
            }
        } catch (error) {
            showToastMessage("An error occurred while fetching topics.", "error");
        } finally {
            setLoading(false);
        }
    }, [token, dispatch]);

    useEffect(() => {
        fetchTopics();
    }, [fetchTopics]);

    useEffect(() => {
        if (topics.length > 0 && location.state?.openedFolder) {
            const selectedFolder = topics.find((topic) => topic.name === location.state.openedFolder);
            if (selectedFolder) {
                setCurrentFolder(location.state.openedFolder);
                setSubtopics(selectedFolder.subtopics || []);
            }
        }
    }, [topics, location.state]);

    const handleFolderClick = (folder: string) => {
        const selectedFolder = topics.find((topic) => topic.name === folder);
        if (selectedFolder?.subtopics?.length) {
            setCurrentFolder(folder);
            setSubtopics(selectedFolder.subtopics);
        } else {
            navigate(`/ques-folders/${folder}/1`);
        }
    };

    const handleSubtopicClick = (subtopic: string) => {
        navigate(`/ques-folders/${currentFolder}/${subtopic}/1`);
    };

    const handleBack = () => {
        setCurrentFolder(null);
        setSubtopics([]);
    };

    const handleAddItem = () => {
        setIsModalVisible(true);
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
        setNewItemName("");
    };

    const handleCreate = async () => {
        if (!newItemName.trim()) {
            showToastMessage("Name cannot be empty", "error");
            return;
        }

        setIsSubmitting(true);
        try {
            const endpoint = currentFolder
                ? `${baseUrl}${API_ENDPOINTS.CREATE_SUBTOPIC}`
                : `${baseUrl}${API_ENDPOINTS.CREATE_TOPIC}`;

            const payload = currentFolder
                ? { topic_name: currentFolder, subtopic_name: newItemName }
                : { topic_name: newItemName, subtopic_name: "General" }; // Default subtopic for new topic

            const response = await fetch(endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                showToastMessage(`${currentFolder ? "Subtopic" : "Topic"} created successfully!`, "success");
                handleModalCancel();
                fetchTopics();
                if (currentFolder) {
                    setSubtopics([...subtopics, newItemName]);
                }
            } else {
                const data = await response.json();
                showToastMessage(data.error || `Failed to create ${currentFolder ? "subtopic" : "topic"}`, "error");
            }
        } catch (error) {
            showToastMessage("An error occurred.", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEditItem = (e: React.MouseEvent, name: string, isSubtopic: boolean) => {
        e.stopPropagation();
        setItemToRename({ name, isSubtopic });
        setNewName(name);
        setIsRenameModalVisible(true);
    };

    const handleRenameCancel = () => {
        setIsRenameModalVisible(false);
        setItemToRename(null);
        setNewName("");
    };

    const handleRenameSubmit = async () => {
        if (!itemToRename) return;
        if (!newName.trim() || newName === itemToRename.name) {
            handleRenameCancel();
            return;
        }

        setIsSubmitting(true);
        try {
            const isSubtopic = itemToRename.isSubtopic;
            const endpoint = isSubtopic
                ? `${baseUrl}${API_ENDPOINTS.RENAME_SUBTOPIC_BY_NAME}/${encodeURIComponent(currentFolder || "")}/${encodeURIComponent(itemToRename.name)}`
                : `${baseUrl}${API_ENDPOINTS.RENAME_TOPIC_BY_NAME}/${encodeURIComponent(itemToRename.name)}`;

            const payload = isSubtopic
                ? { newSubtopicName: newName }
                : { newTopicName: newName };

            const response = await fetch(endpoint, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                showToastMessage(`${isSubtopic ? "Subtopic" : "Topic"} renamed successfully!`, "success");
                await fetchTopics();
                if (isSubtopic) {
                    setSubtopics(subtopics.map(s => s === itemToRename.name ? newName : s));
                }
                handleRenameCancel();
            } else {
                const data = await response.json();
                showToastMessage(data.error || `Failed to rename ${isSubtopic ? "subtopic" : "topic"}`, "error");
            }
        } catch (error) {
            showToastMessage("An error occurred during rename.", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDragStart = (e: React.DragEvent, name: string, isSubtopic: boolean) => {
        e.dataTransfer.setData("text/plain", name);
        setDraggingItem({ name, isSubtopic });
    };

    const handleDragEnd = () => {
        setDraggingItem(null);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move"; // Shows the move cursor
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        if (draggingItem) {
            handleDelete(draggingItem.name, draggingItem.isSubtopic);
            setDraggingItem(null);
        }
    };

    const handleDelete = async (name: string, isSubtopic: boolean) => {
        if (!window.confirm(`Are you sure you want to delete the ${isSubtopic ? 'subtopic' : 'topic'} "${name}"?`)) {
            return;
        }

        try {
            const endpoint = isSubtopic
                ? `${baseUrl}${API_ENDPOINTS.DELETE_SUBTOPIC_BY_NAME}/${encodeURIComponent(currentFolder || "")}/${encodeURIComponent(name)}`
                : `${baseUrl}${API_ENDPOINTS.DELETE_TOPIC_BY_NAME}/${encodeURIComponent(name)}`;

            const response = await fetch(endpoint, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok) {
                showToastMessage(`${isSubtopic ? "Subtopic" : "Topic"} deleted successfully!`, "success");
                fetchTopics();
                if (isSubtopic) {
                    setSubtopics(subtopics.filter(s => s !== name));
                }
            } else {
                const data = await response.json();
                showToastMessage(data.error || `Failed to delete ${isSubtopic ? 'subtopic' : 'topic'}`, "error");
            }
        } catch (error) {
            showToastMessage("An error occurred during deletion.", "error");
        }
    };

    return (
        <div className="w-full h-full bg-transparent">
            <div className="QuestionFolders">
                <div className='ques-bank-heading'>
                    <div onClick={handleBack} className="back-button">
                        <img
                            className={currentFolder ? "" : "hidden"}
                            src={BackBtn}
                            alt="Back Button"
                        />
                    </div>
                    Question Bank
                </div>

                {draggingItem && (
                    <div
                        className="trash-delete-zone fixed-trash-zone"
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                    >
                        <div className="trash-icon-ring">
                            <FaTrash size={32} color="#ff4d4f" />
                        </div>
                        <p>Drop here to delete</p>
                    </div>
                )}

                {loading ? (
                    <p style={{ fontSize: "1.5rem", color: "lightgray" }}>Loading...</p>
                ) : (
                    <div className='ques-folders'>
                        {currentFolder ? (
                            <>
                                {subtopics.map((subtopic) => (
                                    <div
                                        className="each-folder"
                                        key={subtopic}
                                        onClick={() => handleSubtopicClick(subtopic)}
                                        draggable="true"
                                        onDragStart={(e) => handleDragStart(e, subtopic, true)}
                                        onDragEnd={handleDragEnd}
                                        title="Drag to delete"
                                        style={{ cursor: 'grab' }}
                                    >
                                        <img src={FolderIcon} alt="Folder Icon" className="folder-icon" draggable={false} />
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                            <p className="foldername" style={{ margin: 0 }}>{subtopic}</p>
                                            <FaEdit
                                                className="edit-folder-icon"
                                                onClick={(e) => handleEditItem(e, subtopic, true)}
                                                title="Rename Subtopic"
                                                style={{ cursor: 'pointer', color: '#555', fontSize: '16px', flexShrink: 0 }}
                                            />
                                        </div>
                                    </div>
                                ))}
                                <div className="each-folder add-folder" onClick={handleAddItem}>
                                    <div className="add-icon-wrap">
                                        <FaPlus size={30} color="#74B0FA" />
                                    </div>
                                    <p className="foldername">Add Subtopic</p>
                                </div>
                            </>
                        ) : (
                            <>
                                {topics.map((folder) => (
                                    <div
                                        className="each-folder"
                                        key={folder.name}
                                        onClick={() => handleFolderClick(folder.name)}
                                        draggable="true"
                                        onDragStart={(e) => handleDragStart(e, folder.name, false)}
                                        onDragEnd={handleDragEnd}
                                        title="Drag to delete"
                                        style={{ cursor: 'grab' }}
                                    >
                                        <img src={FolderIcon} alt="Folder Icon" className="folder-icon" draggable={false} />
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                            <p className="foldername" style={{ margin: 0 }}>{folder.name}</p>
                                            <FaEdit
                                                className="edit-folder-icon"
                                                onClick={(e) => handleEditItem(e, folder.name, false)}
                                                title="Rename Topic"
                                                style={{ cursor: 'pointer', color: '#555', fontSize: '16px', flexShrink: 0 }}
                                            />
                                        </div>
                                    </div>
                                ))}
                                <div className="each-folder add-folder" onClick={handleAddItem}>
                                    <div className="add-icon-wrap">
                                        <FaPlus size={30} color="#74B0FA" />
                                    </div>
                                    <p className="foldername">Add Topic</p>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            <Modal
                title={currentFolder ? `Add Subtopic to ${currentFolder}` : "Add New Topic"}
                open={isModalVisible}
                onOk={handleCreate}
                onCancel={handleModalCancel}
                confirmLoading={isSubmitting}
                okText="Create"
            >
                <div style={{ marginTop: "20px" }}>
                    <p>{currentFolder ? "Subtopic Name" : "Topic Name"}</p>
                    <Input
                        placeholder={`Enter ${currentFolder ? "subtopic" : "topic"} name`}
                        value={newItemName}
                        onChange={(e) => setNewItemName(e.target.value)}
                        onPressEnter={handleCreate}
                    />
                </div>
            </Modal>

            {/* Rename Modal */}
            <Modal
                title={`Rename ${itemToRename?.isSubtopic ? "Subtopic" : "Topic"}`}
                open={isRenameModalVisible}
                onOk={handleRenameSubmit}
                onCancel={handleRenameCancel}
                confirmLoading={isSubmitting}
                okText="Rename"
            >
                <div style={{ marginTop: "20px" }}>
                    <p>New Name</p>
                    <Input
                        placeholder="Enter new name"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onPressEnter={handleRenameSubmit}
                        autoFocus
                    />
                </div>
            </Modal>
        </div>
    );
}
